import type { UserReference } from './User';

/**
 * Base resource model that all survey templates extend from.
 * Includes common fields like ID, account/creator info, and timestamps.
 */
export interface BaseResourceModel {
	id: string;
	accountId?: string;
	creatorId?: string;
	creator?: UserReference;
	creationDate: string;
	lastModifiedDate: string;
}

/**
 * Template status values for survey templates.
 * Controls visibility and lifecycle of templates.
 */
export enum TemplateStatus {
	Draft = 0,
	Published = 1,
	Archived = 2,
	Disabled = 3,
}

/**
 * Template scope determines who can access the template.
 */
export enum TemplateScope {
	User = 0, // Personal template (only creator can see)
	Account = 1, // Account-wide template
	Industry = 2, // Industry-wide template
	System = 3, // System template (Levitate-provided)
}

/**
 * Full survey template with complete survey definition.
 * Used when creating surveys from templates.
 */
export interface ISurveyTemplate extends BaseResourceModel {
	name: string;
	summary: string;
	keywords: string[];
	scope: TemplateScope;
	enabled: boolean;
	surveyDefinition: any; // Will be ICustomSurvey once we import it
	parentTemplateId: string | null;
	status: TemplateStatus;
	industries?: string[];
}

/**
 * Lightweight template card for list views.
 * Excludes heavy surveyDefinition field for performance.
 */
export interface ISurveyTemplateCard extends BaseResourceModel {
	_type: 'SurveyTemplateCard';
	name: string;
	summary: string;
	keywords: string[];
	scope: TemplateScope;
	status: TemplateStatus;
}

/**
 * Admin view of template with per-industry status mapping.
 * Shows which industries the template is published/archived in.
 */
export interface ISystemSurveyTemplate {
	template: ISurveyTemplate;
	industries: Record<string, TemplateStatus>; // industryName -> status
}

/**
 * Request body for using a survey template to create a new survey.
 */
export interface IUseSurveyTemplateRequest {
	name: string;
}

/**
 * Generic paged collection response from API.
 * Used for browse endpoints with pagination.
 */
export interface IPagedCollection<T> {
	values: T[];
	pageToken: string | null;
	totalCount: number | null;
}
